// BusinessWallet.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react"
import { useParams, useNavigate } from "react-router-dom"
import axios from "axios"
import apiURl from "url" // you said you already import it like this
import { Container, UncontrolledTooltip, Badge } from "reactstrap"
import { useReactToPrint } from "react-to-print"
import PrintBusinessWallet from "./PrintBusinessWallet"
import Select from "react-select"

export default function BusinessWallet() {
  const { id: routeId } = useParams()
  const navigate = useNavigate()

  const printRef = useRef(null)
  const [isPrinting, setIsPrinting] = useState(false)

  const currentUserId = localStorage.getItem("login_id")
  const [businesses, setBusinesses] = useState([])
  const [isLoadingBusinesses, setIsLoadingBusinesses] = useState(false)

  const [selectedBizId, setSelectedBizId] = useState("")
  const [from, setFrom] = useState("") // YYYY-MM-DD
  const [to, setTo] = useState("")

  const [loadingData, setLoadingData] = useState(false)
  const [summary, setSummary] = useState(null)
  const [ledger, setLedger] = useState([])

  //handle Payout
  const [payouts, setPayouts] = useState([])
  const [showPayout, setShowPayout] = useState(false)
  const [payoutAmount, setPayoutAmount] = useState("")
  const [payoutRef, setPayoutRef] = useState("")
  const [payoutNote, setPayoutNote] = useState("")
  const [savingPayout, setSavingPayout] = useState(false)

  // fetch payouts for selected business + date range
  const fetchPayouts = useCallback(async () => {
    if (!selectedBizId) return
    try {
      const params = { businessId: selectedBizId }
      if (from) params.from = from
      if (to) params.to = to
      const r = await axios.get(`${apiURl}/wallet/payouts`, { params })
      setPayouts(r.data?.data ?? [])
    } catch (e) {
      console.error("Payouts fetch error:", e)
      setPayouts([])
    }
  }, [selectedBizId, from, to])

  //handle Adjustments
  const [adjustments, setAdjustments] = useState([])
  const [showAdj, setShowAdj] = useState(false)
  const [adjAmount, setAdjAmount] = useState("") // positive=credit, negative=debit
  const [adjReason, setAdjReason] = useState("")
  const [adjLeadId, setAdjLeadId] = useState("") // optional
  const [savingAdj, setSavingAdj] = useState(false)

  // fetch with businessId/from/to
  const fetchAdjustments = useCallback(async () => {
    if (!selectedBizId) return
    try {
      const params = { businessId: selectedBizId }
      if (from) params.from = from
      if (to) params.to = to
      const r = await axios.get(`${apiURl}/wallet/adjustments`, { params })
      setAdjustments(r.data?.data ?? [])
    } catch (e) {
      console.error("Adjustments fetch error:", e)
      setAdjustments([])
    }
  }, [selectedBizId, from, to])

  // ---------- fetch businesses ----------
  const getAllBusinesses = useCallback(async () => {
    try {
      setIsLoadingBusinesses(true)
      const res = await axios.get(`${apiURl}/getAllBusinesses`)
      if (res.data?.status === "success") {
        setBusinesses(res.data.businesses || [])
      } else {
        console.error("Failed to fetch businesses:", res.data?.message)
      }
    } catch (e) {
      console.error("Error fetching businesses:", e)
    } finally {
      setIsLoadingBusinesses(false)
    }
  }, [])

  useEffect(() => {
    getAllBusinesses()
  }, [getAllBusinesses])

  // Only Approved businesses
  const approvedBusinesses = useMemo(
    () => businesses.filter(b => b?.accountStatus === "Approved"),
    [businesses]
  )

  // build options with consistent types
  const options = useMemo(
    () =>
      approvedBusinesses.map(b => ({
        value: String(b.id), // <-- keep as string
        label: b.businessName || String(b.id),
      })),
    [approvedBusinesses]
  )

  // derive the selected option from the id
  const selectedOption = useMemo(
    () => options.find(o => o.value === String(selectedBizId)) || null,
    [options, selectedBizId]
  )

  // When businesses list or routeId changes, auto-select
  useEffect(() => {
    if (!approvedBusinesses.length) return

    if (routeId) {
      setSelectedBizId(String(routeId))
    } else {
      const first = approvedBusinesses[0]
      setSelectedBizId(first ? String(first.id) : "")
    }
  }, [approvedBusinesses, routeId])

  // keep URL in sync when user selects a biz
  const onChangeBusiness = opt => {
    const val = opt?.value || ""
    setSelectedBizId(val)
    navigate(val ? `/cod-business/wallet/${val}` : `/cod-business/wallet`, {
      replace: true,
    })
  }

  // ---------- fetch wallet data ----------
  const fetchWalletData = useCallback(async () => {
    if (!selectedBizId) return
    setLoadingData(true)
    try {
      const params = { businessId: selectedBizId }
      if (from) params.from = from
      if (to) params.to = to

      const [sumRes, txRes, poRes, adjRes] = await Promise.all([
        axios.get(`${apiURl}/wallet/summary`, { params }),
        axios.get(`${apiURl}/wallet/transactions`, { params }),
        axios.get(`${apiURl}/wallet/payouts`, { params }),
        axios.get(`${apiURl}/wallet/adjustments`, { params }),
      ])
      setSummary(sumRes.data?.data ?? null)
      setLedger(txRes.data?.data ?? [])
      setPayouts(poRes.data?.data ?? [])
      setAdjustments(adjRes.data?.data ?? [])
    } catch (e) {
      console.error("Wallet fetch error:", e)
      setSummary(null)
      setLedger([])
      setPayouts([])
      setAdjustments([])
    } finally {
      setLoadingData(false)
    }
  }, [selectedBizId, from, to])

  useEffect(() => {
    fetchWalletData()
    fetchPayouts()
    fetchAdjustments()
  }, [fetchWalletData])

  const asPKR = v =>
    Number(v || 0).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })

  //Print
  const waitNextFrame = () =>
    new Promise(resolve =>
      requestAnimationFrame(() => requestAnimationFrame(resolve))
    )

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `BusinessWallet_${selectedBizId}_${from || "all"}_${
      to || "all"
    }`,
    onAfterPrint: () => setIsPrinting(false),
    removeAfterPrint: true,
  })

  // Optional: ensure latest summary/ledger/payouts/adjustments before print
  const onClickPrint = async () => {
    if (!selectedBizId) return
    setIsPrinting(true)
    try {
      // refresh the data for the current filters
      await fetchWalletData()
      await waitNextFrame() // let React paint hidden print DOM
      handlePrint()
    } catch {
      setIsPrinting(false)
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="container-fluid">
            {/* Print progress overlay */}
            {isPrinting && (
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Preparing print"
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(17, 24, 39, 0.45)", // slate-900/45
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 9999,
                }}
              >
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 12,
                    padding: "18px 20px",
                    minWidth: 260,
                    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                    textAlign: "center",
                  }}
                >
                  <div
                    className="spinner-border text-primary"
                    role="status"
                    aria-hidden="true"
                  />
                  <div style={{ fontWeight: 700, marginTop: 10 }}>
                    Preparing print…
                  </div>
                  <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>
                    Fetching Wallet Balance, Payouts, Adjusments
                  </div>
                </div>
              </div>
            )}
            {/* Header / Filters */}
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex gap-2">
                <h4 className="mb-0">Business Wallet</h4>
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={onClickPrint}
                  disabled={!selectedBizId || loadingData || isPrinting}
                  title="Print wallet statement"
                >
                  {isPrinting ? "Preparing…" : "🖨️ Print"}
                </button>
              </div>
              <div className="d-flex gap-2 align-items-center">
                {/* Business selector */}
                <Select
                  className="select2-selection"
                  style={{ width: "600px" }}
                  value={selectedOption} // <-- controlled by derived option
                  onChange={onChangeBusiness}
                  disabled={isLoadingBusinesses}
                  options={options}
                  styles={{
                    container: base => ({ ...base, width: 320 }),
                    control: (base, state) => ({
                      ...base,
                      minHeight: 30, // reduce height
                      height: 30,
                      borderRadius: 6,
                      boxShadow: state.isFocused ? "0 0 0 1px #2684FF" : "none",
                      borderColor: state.isFocused
                        ? "#2684FF"
                        : base.borderColor,
                    }),
                    valueContainer: base => ({
                      ...base,
                      padding: "0 8px",
                      height: 30,
                    }),
                    input: base => ({ ...base, margin: 0, padding: 0 }),
                    indicatorsContainer: base => ({ ...base, height: 30 }),
                    dropdownIndicator: base => ({ ...base, padding: 4 }),
                    clearIndicator: base => ({ ...base, padding: 4 }),
                    singleValue: base => ({ ...base, fontSize: 13 }),
                    menu: base => ({ ...base, zIndex: 9 }),
                  }}
                />

                {/* Date range */}
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={from}
                  onChange={e => setFrom(e.target.value)}
                  style={{ width: 160 }}
                />
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={to}
                  onChange={e => setTo(e.target.value)}
                  style={{ width: 160 }}
                />
                <button
                  className="btn btn-primary btn-sm"
                  onClick={fetchWalletData}
                  disabled={!selectedBizId || loadingData}
                >
                  {loadingData ? "Loading..." : "Apply"}
                </button>
              </div>
            </div>

            {!selectedBizId ? (
              <div className="alert alert-info">
                Select a business to view wallet.
              </div>
            ) : (
              <>
                {/* KPIs */}
                <div className="row g-3 mb-3">
                  <div className="col-6 col-md-3">
                    <div className="card shadow-sm">
                      <div className="card-body">
                        <div className="text-muted small">Total Leads</div>
                        <div className="fs-4 fw-bold">
                          {summary?.leads_count ?? "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="card shadow-sm">
                      <div className="card-body">
                        <div className="text-muted small">
                          Priced & Assigned
                        </div>
                        <div className="fs-4 fw-bold">
                          {summary?.priced_leads_count ?? "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="card shadow-sm">
                      <div className="card-body">
                        <div className="text-muted small">COD Collected</div>
                        <div className="fs-4 fw-bold">
                          PKR {asPKR(summary?.cod_collected)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="card shadow-sm">
                      <div className="card-body">
                        <div className="text-muted small">Service Charges</div>
                        <div className="fs-4 fw-bold">
                          PKR {asPKR(summary?.service_charges)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-6 col-md-3">
                    <div className="card shadow-sm">
                      <div className="card-body">
                        <div className="row">
                          <div className="col-6 col-md-6">
                            <div className="text-muted small">Adjustments</div>
                            <div className="fs-4 fw-bold">
                              PKR {asPKR(summary?.adjustments)}
                            </div>
                          </div>
                          <div className="col-6 col-md-6">
                            <button
                              className="btn btn-outline-danger btn-sm mt-2 float-end"
                              disabled={!selectedBizId}
                              onClick={() => setShowAdj(true)}
                              title="Create a manual adjustment (credit/debit)"
                            >
                              New Adjustment
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="card shadow-sm">
                      <div className="card-body">
                        <div className="row">
                          <div className="col-6 col-md-6">
                            <div className="text-muted small">Payouts</div>
                            <div className="fs-4 fw-bold">
                              PKR {asPKR(summary?.payouts)}
                            </div>
                          </div>
                          <div className="col-6 col-md-6">
                            <button
                              className="btn btn-outline-danger btn-sm mt-2 float-end"
                              disabled={!selectedBizId}
                              onClick={() => setShowPayout(true)}
                              title="Record a payout to this business"
                            >
                              Make Payout
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="card shadow-sm">
                      <div className="card-body d-flex justify-content-between align-items-center">
                        <div>
                          <div className="text-muted small">
                            Wallet Balance (Net Payable to Business)
                          </div>
                          <div className="fs-3 fw-bold">
                            PKR {asPKR(summary?.wallet_balance)}
                          </div>
                        </div>
                        {/* Optional export */}
                        {/* <button className="btn btn-outline-secondary btn-sm">Export CSV</button> */}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ledger */}
                <div className="card shadow-sm">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <div className="fw-bold">Transactions</div>

                    <div className="text-muted small">
                      {summary?.range
                        ? `${summary.range.from} → ${summary.range.to}`
                        : ""}
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 170 }}>Timestamp</th>
                          <th style={{ width: 130 }}>Type</th>
                          <th>Lead</th>
                          <th>Order</th>
                          <th className="text-end" style={{ width: 160 }}>
                            Amount (PKR)
                          </th>
                          <th>Memo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(ledger || []).map((row, i) => (
                          <tr key={i}>
                            <td>{new Date(row.ts).toLocaleString()}</td>
                            <td>
                              {row.type === "COD_IN"
                                ? "COD In"
                                : row.type === "SERVICE_CHARGE"
                                ? "Service Charge"
                                : row.type === "PAYOUT"
                                ? "Payout"
                                : "Adjustment"}
                            </td>
                            <td>
                              {row.cod_lead_uni_id ||
                                (row.lead_id ? `#${row.lead_id}` : "—")}
                            </td>
                            <td>{row.order_id || "—"}</td>
                            <td
                              className={`text-end ${
                                Number(row.amount) < 0
                                  ? "text-danger"
                                  : "text-success"
                              }`}
                            >
                              {asPKR(row.amount)}
                            </td>
                            <td>{row.memo}</td>
                          </tr>
                        ))}
                        {!loadingData && (!ledger || !ledger.length) && (
                          <tr>
                            <td
                              colSpan={6}
                              className="text-center text-muted py-4"
                            >
                              No transactions in this range
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* Recent Payouts */}
                <div className="row g-3 mb-4">
                  <div className="col-12 col-lg-6">
                    <div className="card shadow-sm">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <div className="fw-bold">Recent Payouts</div>
                        <div className="text-muted small">
                          {payouts?.length ? `${payouts.length} in range` : "—"}
                        </div>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-sm mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: 170 }}>Timestamp</th>
                              <th className="text-end" style={{ width: 140 }}>
                                Amount
                              </th>
                              <th style={{ width: 160 }}>Reference</th>
                              <th>Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(payouts || []).slice(0, 6).map(p => (
                              <tr key={p.id}>
                                <td>
                                  {new Date(p.created_at).toLocaleString()}
                                </td>
                                <td className="text-end text-danger">
                                  - {asPKR(p.amount)}
                                </td>
                                <td>{p.reference || "—"}</td>
                                <td
                                  className="text-truncate"
                                  style={{ maxWidth: 280 }}
                                >
                                  {p.note || "—"}
                                </td>
                              </tr>
                            ))}
                            {(!payouts || payouts.length === 0) && (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="text-center text-muted py-3"
                                >
                                  No payouts in this range
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {/*Recent Adjustments*/}
                    <div className="col-12 col-lg-12">
                      <div className="card shadow-sm">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <div className="fw-bold">Recent Adjustments</div>
                          <div className="text-muted small">
                            {adjustments?.length
                              ? `${adjustments.length} in range`
                              : "—"}
                          </div>
                        </div>
                        <div className="table-responsive">
                          <table className="table table-sm mb-0">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: 170 }}>Timestamp</th>
                                <th style={{ width: 120 }}>Lead</th>
                                <th>Reason</th>
                                <th className="text-end" style={{ width: 140 }}>
                                  Amount
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {(adjustments || []).slice(0, 6).map(a => (
                                <tr key={a.id}>
                                  <td>
                                    {new Date(a.created_at).toLocaleString()}
                                  </td>
                                  <td>{a.lead_id ? `#${a.lead_id}` : "—"}</td>
                                  <td
                                    className="text-truncate"
                                    style={{ maxWidth: 280 }}
                                  >
                                    {a.reason}
                                  </td>
                                  <td
                                    className={`text-end ${
                                      Number(a.amount) < 0
                                        ? "text-danger"
                                        : "text-success"
                                    }`}
                                  >
                                    {Number(a.amount) < 0 ? "- " : "+ "}
                                    {Number(
                                      Math.abs(a.amount)
                                    ).toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                              {(!adjustments || adjustments.length === 0) && (
                                <tr>
                                  <td
                                    colSpan={4}
                                    className="text-center text-muted py-3"
                                  >
                                    No adjustments in this range
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* (Optional) Quick balance explainer */}
                  <div className="col-12 col-lg-6">
                    <div className="card shadow-sm">
                      <div className="card-body">
                        <div className="fw-bold mb-2">Balance Breakdown</div>
                        <div className="d-flex justify-content-between">
                          <span>COD Collected</span>
                          <span>PKR {asPKR(summary?.cod_collected)}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Service Charges</span>
                          <span className="text-danger">
                            - PKR {asPKR(summary?.service_charges)}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Payouts</span>
                          <span className="text-danger">
                            - PKR {asPKR(summary?.payouts)}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Adjustments</span>
                          <span
                            className={
                              Number(summary?.adjustments) < 0
                                ? "text-danger"
                                : "text-success"
                            }
                          >
                            {Number(summary?.adjustments) < 0 ? "- " : "+ "}PKR{" "}
                            {asPKR(Math.abs(summary?.adjustments || 0))}
                          </span>
                        </div>
                        <hr />
                        <div className="d-flex justify-content-between fw-bold">
                          <span>Wallet Balance</span>
                          <span>PKR {asPKR(summary?.wallet_balance)}</span>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-success btn-sm float-end"
                      onClick={onClickPrint}
                      disabled={!selectedBizId || loadingData || isPrinting}
                      title="Print wallet statement"
                    >
                      {isPrinting ? "Preparing…" : "🖨️ Print"}
                    </button>
                  </div>
                </div>

                {/* Payout Modal */}
                {showPayout && (
                  <div
                    className="modal d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={{ background: "rgba(0,0,0,0.45)" }}
                    aria-modal="true"
                    aria-labelledby="payoutModalTitle"
                  >
                    <div className="modal-dialog modal-sm modal-dialog-centered">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h6 className="modal-title" id="payoutModalTitle">
                            Record Payout
                          </h6>
                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setShowPayout(false)}
                            aria-label="Close"
                          />
                        </div>
                        <div className="modal-body">
                          <div className="mb-2">
                            <label className="form-label form-label-sm">
                              Amount (PKR)
                            </label>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              className="form-control form-control-sm"
                              value={payoutAmount}
                              onChange={e => setPayoutAmount(e.target.value)}
                              placeholder="e.g. 25000"
                            />
                          </div>
                          <div className="mb-2">
                            <label className="form-label form-label-sm">
                              Reference (optional)
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={payoutRef}
                              onChange={e => setPayoutRef(e.target.value)}
                              placeholder="Bank ref / slip #"
                            />
                          </div>
                          <div>
                            <label className="form-label form-label-sm">
                              Note (optional)
                            </label>
                            <textarea
                              className="form-control form-control-sm"
                              rows={2}
                              value={payoutNote}
                              onChange={e => setPayoutNote(e.target.value)}
                              placeholder="Any details to remember"
                            />
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button
                            className="btn btn-light btn-sm"
                            onClick={() => setShowPayout(false)}
                            disabled={savingPayout}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-success btn-sm"
                            onClick={async () => {
                              const amt = Number(payoutAmount)
                              if (!amt || amt <= 0)
                                return alert("Enter a valid amount")
                              try {
                                setSavingPayout(true)
                                await axios.post(`${apiURl}/wallet/payouts`, {
                                  businessId: selectedBizId,
                                  amount: amt,
                                  reference: payoutRef || null,
                                  note: payoutNote || null,
                                  createdBy: currentUserId,
                                })
                                // clear + close
                                setPayoutAmount("")
                                setPayoutRef("")
                                setPayoutNote("")
                                setShowPayout(false)
                                // refresh summary, ledger, payouts
                                await fetchWalletData()
                              } catch (e) {
                                console.error("Create payout failed:", e)
                                alert("Failed to save payout")
                              } finally {
                                setSavingPayout(false)
                              }
                            }}
                            disabled={savingPayout}
                          >
                            {savingPayout ? "Saving..." : "Save Payout"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/*Adjusment Modal*/}
                {showAdj && (
                  <div
                    className="modal d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={{ background: "rgba(0,0,0,0.45)" }}
                  >
                    <div className="modal-dialog modal-sm modal-dialog-centered">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h6 className="modal-title">New Adjustment</h6>
                          <button
                            className="btn-close"
                            onClick={() => setShowAdj(false)}
                          />
                        </div>
                        <div className="modal-body">
                          <div className="mb-2">
                            <label className="form-label form-label-sm">
                              Amount (PKR)
                            </label>
                            <input
                              type="number"
                              step="1"
                              className="form-control form-control-sm"
                              value={adjAmount}
                              onChange={e => setAdjAmount(e.target.value)}
                              placeholder="+1000 credit to business, -500 debit"
                            />
                            <div className="form-text">
                              Use <b>positive</b> for credit to business,{" "}
                              <b>negative</b> for debit.
                            </div>
                          </div>
                          <div className="mb-2">
                            <label className="form-label form-label-sm">
                              Reason
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={adjReason}
                              onChange={e => setAdjReason(e.target.value)}
                              placeholder="e.g., Fee waived for RTD #CL-1234"
                            />
                          </div>
                          <div>
                            <label className="form-label form-label-sm">
                              Lead (optional)
                            </label>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={adjLeadId}
                              onChange={e => setAdjLeadId(e.target.value)}
                              placeholder="Lead ID if relevant"
                            />
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button
                            className="btn btn-light btn-sm"
                            onClick={() => setShowAdj(false)}
                            disabled={savingAdj}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-warning btn-sm"
                            disabled={savingAdj}
                            onClick={async () => {
                              const amt = Number(adjAmount)
                              if (!amt || amt === 0)
                                return alert("Enter a non-zero amount")
                              if (!adjReason || adjReason.trim().length < 3)
                                return alert("Reason required")
                              try {
                                setSavingAdj(true)
                                await axios.post(
                                  `${apiURl}/wallet/adjustments`,
                                  {
                                    businessId: selectedBizId,
                                    amount: amt,
                                    reason: adjReason.trim(),
                                    leadId: adjLeadId
                                      ? Number(adjLeadId)
                                      : null,
                                    createdBy: currentUserId,
                                  }
                                )
                                setAdjAmount("")
                                setAdjReason("")
                                setAdjLeadId("")
                                setShowAdj(false)
                                await fetchWalletData() // refresh KPIs, ledger, payouts, adjustments
                              } catch (e) {
                                console.error("Create adjustment failed:", e)
                                alert("Failed to save adjustment")
                              } finally {
                                setSavingAdj(false)
                              }
                            }}
                          >
                            {savingAdj ? "Saving..." : "Save Adjustment"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Hidden print content */}
                <div
                  aria-hidden
                  style={{
                    position: "fixed",
                    inset: 0,
                    width: 0,
                    height: 0,
                    overflow: "hidden",
                    visibility: "hidden",
                    zIndex: -1,
                  }}
                >
                  <div ref={printRef}>
                    <PrintBusinessWallet
                      business={approvedBusinesses.find(
                        b => String(b.id) === String(selectedBizId)
                      )}
                      summary={summary}
                      ledger={ledger}
                      payouts={payouts}
                      adjustments={adjustments}
                      issuedAt={new Date().toISOString()}
                      from={from}
                      to={to}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </Container>
      </div>
    </React.Fragment>
  )
}
